/**
 * What the settings window shows, and everything a person can do to it.
 *
 * One backing view model behind every platform's settings screen, for §05 of
 * docs/kmp-sharing-architecture.html's reason and for a sharper one this surface has of its
 * own: **the states are the hard part here, not the controls.** Loading, ready, saving,
 * refused-with-a-sentence-from-the-server, testing the connection, tested-and-it-worked,
 * tested-and-it-did-not, queueing a backfill, a backfill running, stopping one. Seven
 * controls are seven lines of layout on any platform; those states are where a screen goes
 * wrong, and getting them wrong differently on three platforms is how one of them ends up
 * swallowing a refusal.
 *
 * One of them is not this model's to hold at all, and that is the point of it: **whether a
 * backfill is running is counted by the server**, polled every couple of seconds, and derived
 * from the counts — see [State.Ready.isBackfillRunning]. The flag it replaces meant "the
 * enqueue POST is in flight", and went false a second after the press with hours of work
 * still to go.
 *
 * ── Save-on-change, and the footer that is deliberately not built ────────────
 *
 * The design draws a footer bar with **Cancel** and **Save**. This model does not have
 * them, and the fork is worth stating because it is a visible departure from what was
 * drawn.
 *
 * A settings *window* is not a modal. It can be left open beside the list it explains, it
 * survives a reload, and the toolkit's × is its dismissal — so there is no moment at which
 * a person "closes the form", and a Save button on a surface with no such moment is a
 * button whose job is to be forgotten. Worse, *Cancel* would have to mean "put back what
 * was there when this window opened", which is a baseline the window has to hold for as
 * long as it is open and which nothing else in this app keeps. The smaller option, and the
 * one that cannot leave a stale footer lying about a screen somebody wandered away from, is
 * that **every control writes when it is changed**.
 *
 * That decision puts one obligation on this file, and it is the whole reason the machinery
 * below exists: **a settings screen may never silently drop a change.** Three things make
 * that true here.
 *
 *  1. **Writes are launched on [scope], not on the scope [run] is collected on.** Closing
 *     the window a moment after flicking a switch must still finish the write — the same
 *     split `EditBookmarkBackingViewModel` makes for a save, for the same reason.
 *  2. **Writes are serialised by a mutex, and each one sends the form as it stands when it
 *     acquires it.** Two flicks in a row are two writes that cannot invert, and a pair that
 *     coalesces still ends on the newer value rather than on whichever response came back
 *     last.
 *  3. **A refusal is shown, never swallowed.** The server's own sentence goes over the
 *     form, and the form keeps what the person chose so they can see what it is about. The
 *     one thing that must not happen is a control that silently snaps back with no
 *     explanation, which is what "revert to the stored value" would look like from the
 *     other side of the screen.
 *
 * ── What this model knows about the key, which is one boolean ────────────────
 *
 * [AccountSettings.aiAvailable]. There is no key field, no *Test key*, no *Remove key* and
 * nothing here that could hold a secret if it wanted to — the key is the operator's
 * `LUNAPIN_KEY_OPENAI`, read at boot, and no route returns it or any part of it. What this
 * model does have is [onTestConnectionRequested], which answers the different and more
 * useful question: not "is something set" but "does the thing that is set work".
 *
 * @see se.soderbjorn.lunapin.clientserver.AccountSettings
 */
package se.soderbjorn.lunapin.client.viewmodel

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.collect
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlin.coroutines.cancellation.CancellationException
import se.soderbjorn.lunapin.client.StorageRepository
import se.soderbjorn.lunapin.client.readableMessage
import se.soderbjorn.lunapin.clientserver.AccountSettings
import se.soderbjorn.lunapin.clientserver.AiUsage
import se.soderbjorn.lunapin.clientserver.EnrichmentProgress
import se.soderbjorn.lunapin.clientserver.McpState
import se.soderbjorn.lunapin.clientserver.SetAccountSettingsRequest
import se.soderbjorn.lunapin.clientserver.TagUse
import se.soderbjorn.lunapin.clientserver.TagVocabularyTargets
import se.soderbjorn.lunapin.clientserver.TidyImpact
import se.soderbjorn.lunapin.clientserver.TidyPair
import se.soderbjorn.lunapin.clientserver.TidyPlan
import se.soderbjorn.lunapin.clientserver.TidyResult
import se.soderbjorn.lunapin.clientserver.TidySelection
import se.soderbjorn.lunapin.clientserver.TidyStatus
import se.soderbjorn.lunapin.clientserver.UndoTidyResult

/**
 * How long between two readings of the enrichment queue while a settings window is open.
 *
 * Two seconds, against a worker that finishes roughly one bookmark every three: anything
 * longer would show a four-hour backfill arriving in visible lumps, and anything shorter
 * would be asking a question whose answer cannot have changed.
 *
 * **Unconditional, unlike `REFRESH_WHILE_AWAITING_MILLIS`**, which arms itself only while a
 * visible row is waiting. The equivalent arming here would be "poll while a backfill is
 * running", and a panel that only asks while it already believes something is happening
 * could never notice a backfill started on another device — which is half of what counting
 * the queue is for. What bounds it instead is the window: one grouped count over one
 * account's bookmarks every two seconds while a settings pane is open, and nothing at all
 * when it is not.
 *
 * `internal` so the test that pins the polling can advance exactly this much rather than
 * guessing at it.
 */
internal const val PROGRESS_POLL_MILLIS = 2_000L

/**
 * What the *Let AI agents act on your bookmarks* switch is called, and what it means.
 *
 * Here rather than in a view because every platform's Connections pane has to say the same
 * thing, and because the second sentence is a *security claim* rather than a caption: an
 * approved agent acts **as the account**, through the same stores the web app reaches, with
 * no narrower permission to fall back on. That is true — see the server's `McpTools` — and
 * it is the sentence somebody needs before flipping the switch, so it must not be reworded
 * per platform into something softer.
 *
 * Not a scope string. "Read/write bookmarks, read/write tags" would be an invented
 * vocabulary describing a thing that has no scopes; the honest description is the account.
 */
const val AGENT_ACCESS_LABEL: String = "Let AI agents act on your bookmarks"

/** See [AGENT_ACCESS_LABEL]. */
const val AGENT_ACCESS_EXPLANATION: String =
    "An agent you approve can search, add, edit and delete bookmarks and change tags, as " +
        "you. It gets no shortcut the app itself does not have, and no more than you can do."

/**
 * Turn an epoch-millis timestamp into "2 minutes ago" and friends.
 *
 * Relative rather than absolute, and only for the Connections list: that list is scanned to
 * answer *is this still in use?*, and "17 Jul 2026, 14:32" does not answer it without
 * arithmetic. Bookmarks are dated the other way round — they are compared with each other,
 * so they want a fixed shape — which is why this is a second formatter rather than a shared
 * one, and why both live where the question is asked rather than in a view.
 *
 * Falls back to a plain day count past a week, where "23 days ago" has stopped being easier
 * to read than the date; the date itself is deliberately not built here, because a locale
 * format in common code is a platform's job and a wrong-looking date is worse than a count.
 *
 * @param millis when it happened, from the server.
 * @param now what to measure against.
 */
internal fun formatRelative(millis: Long, now: Long): String {
    // A browser clock a few seconds behind the server's would otherwise produce "in -3
    // seconds". The server's timestamps are authoritative and the device's is not, so
    // anything in the future is simply "just now".
    val elapsed = now - millis
    if (elapsed < 0) return "just now"
    val seconds = elapsed / 1000
    val minutes = seconds / 60
    val hours = minutes / 60
    val days = hours / 24
    return when {
        seconds < 60 -> "just now"
        minutes < 60 -> "$minutes ${plural(minutes, "minute")} ago"
        hours < 24 -> "$hours ${plural(hours, "hour")} ago"
        else -> "$days ${plural(days, "day")} ago"
    }
}

private fun plural(count: Long, word: String): String = if (count == 1L) word else "${word}s"

/**
 * Backs the settings window.
 *
 * @param storage the session's source of truth and the only route to the server.
 * @param scope where a write, a backfill and a connection test are launched. Injected with
 *   a default for [WorkspaceBackingViewModel]'s reason — a test supplies its own and can
 *   await it — and deliberately **not** the scope [run] is collected on: see the file
 *   header, where that split is the first of the three things keeping a change from being
 *   dropped.
 * @param now what the Connections list's "connected 3 days ago" is measured against.
 *   Injectable so a test can pin it — the alternative is a test that passes for
 *   fifty-nine seconds — and a lambda rather than a `Clock` because it is one number.
 */
@OptIn(kotlin.time.ExperimentalTime::class)
class SettingsBackingViewModel(
    private val storage: StorageRepository,
    private val scope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Main),
    private val now: () -> Long = { kotlin.time.Clock.System.now().toEpochMilliseconds() },
) {
    private val _stateFlow = MutableStateFlow(State(loading = State.Loading))

    /** The complete, render-ready description of the window. */
    val stateFlow = _stateFlow.asStateFlow()

    /**
     * Every reason to fetch, counted — [BookmarkListBackingViewModel]'s mechanism, for its
     * reason: a tick rather than a `Unit`, because a `StateFlow` conflates equal values and
     * two retries in a row must be two fetches.
     */
    private val reloads = MutableStateFlow(0)

    /**
     * Serialises writes against each other.
     *
     * Not against reads: a reload replaces the whole state anyway, and a lock held across a
     * fetch would be a lock held for as long as the network takes. What this prevents is the
     * one thing that actually goes wrong under save-on-change — two `PUT`s in flight at once
     * and the older answer landing last, which would paint the screen with the value the
     * person just moved away from.
     */
    private val writes = Mutex()

    /**
     * Serialises the tidy dialog's recounts against each other.
     *
     * Its own lock rather than a second user of [writes], because the two are unrelated: a
     * settings write and a tidy preview can honestly be in flight at once — somebody may flick
     * a switch while a dialog is open — and one lock would make each wait for the other for no
     * reason. What it prevents is the same thing [writes] prevents, one layer along: three
     * ticks in quick succession are three requests, and the older answer landing last would
     * paint a total for a selection nobody is looking at any more.
     */
    private val tidyCounts = Mutex()

    /**
     * Load everything, then load it again whenever asked — and keep reading the enrichment
     * queue for as long as the window is open. Called once by the platform; never returns.
     *
     * The poll is a child of this function rather than a launch on [scope], and that is the
     * same rule `BookmarkListBackingViewModel.run` states for its timer: **cancelling this
     * cancels the polling**. A timer with a lifetime of its own would keep a closed window
     * asking the server for the rest of the session, which is the invisible sort of leak —
     * nothing breaks, the app is just permanently busier the longer somebody works.
     *
     * It is deliberately the opposite split from the writes, the backfill and the connection
     * test, which are launched on [scope] so that closing the window a moment after pressing
     * something still finishes it. Nobody asked for the next poll, so nobody is owed it.
     */
    @Throws(CancellationException::class, Exception::class)
    suspend fun run() {
        coroutineScope {
            launch { pollProgress() }
            // Collecting a StateFlow delivers its current value first, so the initial load is
            // this rather than a separate call. It never completes, which is what keeps the
            // scope — and with it the poll — open.
            reloads.collect { load() }
        }
    }

    /** Ask again — the retry on the failure panel, and the way out of a lost network. */
    fun onReloadRequested() {
        _stateFlow.value = State(loading = State.Loading)
        reloads.value += 1
    }

    /**
     * Fetch the settings, the month's usage, the queue's progress and the account's tag
     * vocabulary.
     *
     * **Only the settings can fail the screen.** The other three are the panel's numbers and
     * the *Apply by default* menu's rows, and refusing to show a settings window because a
     * usage count did not arrive would be the wrong trade by a distance — the same judgement
     * `EditBookmarkBackingViewModel.load` makes about its tag fetch, for the same reason. A
     * usage panel with nothing in it is a panel of zeroes, which is what a fresh account has
     * anyway.
     */
    private suspend fun load() {
        _stateFlow.value = try {
            State(
                ready = State.Ready(
                    settings = storage.accountSettings(),
                    usage = usageOrEmpty(),
                    progress = progressOrEmpty(),
                    knownTags = knownTags(),
                    tidy = tidyStatusOrEmpty(),
                    mcp = mcpOrNull(),
                ),
            )
        } catch (cancellation: CancellationException) {
            throw cancellation
        } catch (failure: Exception) {
            State(failed = State.Failed(failure.readableMessage(COULD_NOT_LOAD)))
        }
    }

    /** The month's tally, or zeroes — see [load] on why this failure is survivable. */
    private suspend fun usageOrEmpty(): AiUsage =
        try {
            storage.aiUsage()
        } catch (cancellation: CancellationException) {
            throw cancellation
        } catch (_: Exception) {
            AiUsage()
        }

    /**
     * The queue as it stands, or zeroes — see [load].
     *
     * Zeroes are the *idle* panel rather than a blank one, and that is the right way to fail
     * here: the backfill's own numbers come back on the next poll two seconds later, so a
     * momentary "nothing is happening" is a great deal better than a panel that vanishes
     * because one request in a long series did not answer.
     */
    private suspend fun progressOrEmpty(): EnrichmentProgress =
        try {
            storage.enrichmentProgress()
        } catch (cancellation: CancellationException) {
            throw cancellation
        } catch (_: Exception) {
            EnrichmentProgress()
        }

    /** The account's tags, or none — see [load]. */
    private suspend fun knownTags(): List<TagUse> =
        try {
            storage.tags()
        } catch (cancellation: CancellationException) {
            throw cancellation
        } catch (_: Exception) {
            emptyList()
        }

    /**
     * The vocabulary's own counts, or zeroes — see [load].
     *
     * Zeroes draw a panel that offers the ceiling and says nothing about how many tags there
     * are, which is what the panel looked like before this feature existed. That is a better
     * failure than refusing the whole settings window over a count, and better than an
     * *Undo last tidy* that is offered because a request did not answer: `canUndo` defaults
     * false, so the fallback never invites somebody to press a button that would do nothing.
     */
    private suspend fun tidyStatusOrEmpty(): TidyStatus =
        try {
            storage.tidyStatus()
        } catch (cancellation: CancellationException) {
            throw cancellation
        } catch (_: Exception) {
            TidyStatus()
        }

    /**
     * The Connections tab, or **null** — see [load] on why one failure here is survivable and
     * [State.Mcp] on why null is not the same as "off, with nothing connected".
     *
     * The one fallback on this screen that is not a zeroed value, and deliberately: the other
     * three describe a collection, where empty is a truthful reading of a fresh account. This
     * one describes a **security gate**, and a pane that drew *Off* over a failed request
     * would be telling somebody nothing can act as their account without having asked. Null
     * draws a sentence saying it could not be read.
     */
    private suspend fun mcpOrNull(): State.Mcp? =
        try {
            storage.mcpState().asMcp()
        } catch (cancellation: CancellationException) {
            throw cancellation
        } catch (_: Exception) {
            null
        }

    /**
     * A server [McpState] as the pane draws it.
     *
     * The relative dates are turned into strings **here**, against one reading of the clock,
     * rather than in a view: three platforms formatting "connected 3 days ago" is three
     * chances to disagree about what a day is. They do not tick — the strings are as old as
     * the fetch that produced them — which is right for a list nobody watches and refreshes
     * on every press.
     */
    private fun McpState.asMcp(): State.Mcp {
        val timestamp = now()
        return State.Mcp(
            isEnabled = isEnabled,
            serverUrl = serverUrl,
            connections = connections.map {
                State.Mcp.Connection(
                    clientId = it.clientId,
                    name = it.clientName,
                    detail = buildString {
                        append("Connected ${formatRelative(it.connectedAt, timestamp)}")
                        append(" · ")
                        // "never used" rather than leaving the half out: an agent connected
                        // in July that has never called is exactly the row somebody should
                        // revoke, and silence would hide it.
                        append(
                            it.lastUsedAt
                                ?.let { used -> "last used ${formatRelative(used, timestamp)}" }
                                ?: "never used",
                        )
                    },
                )
            },
        )
    }

    /**
     * Read the queue again, for as long as the window is open.
     *
     * Delay first, then fetch: [load] has already asked once by the time this runs, and a
     * loop that fetched first would make every window open with two readings of the same
     * numbers a millisecond apart.
     *
     * **A failed poll changes nothing on screen.** The panel keeps the last numbers it had
     * and the loop keeps its rhythm — a dropped request during a four-hour backfill is
     * ordinary, and a progress display that reset itself to zero over one of them would be
     * reporting a finished backfill that is still running.
     */
    private suspend fun pollProgress() {
        while (true) {
            delay(PROGRESS_POLL_MILLIS)
            val progress = try {
                storage.enrichmentProgress()
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (_: Exception) {
                continue
            }
            _stateFlow.value.ready?.let { publish(it.copy(progress = progress)) }
        }
    }

    // ── The controls ─────────────────────────────────────────────────────────
    //
    // One intent per control, named for the event rather than for the field it happens to
    // write, and every one of them ends in the same place: the form is updated and the whole
    // set is written. There is no *Save*; see the file header.
    //
    // A platform is expected to call these on a **commit** rather than on every frame of a
    // gesture — the release of a slider, the blur of a text field — because each one is a
    // request. That is a rule about where the intent is raised and not about what it does,
    // which is why it is stated here rather than enforced with a timer nobody could test.

    /** *Write summaries* was flicked. */
    fun onSummariesToggled(enabled: Boolean) {
        change { it.copy(aiSummariesEnabled = enabled) }
    }

    /** *Add tags* was flicked. */
    fun onTagsToggled(enabled: Boolean) {
        change { it.copy(aiTagsEnabled = enabled) }
    }

    /**
     * A preset on *Tag vocabulary* was pressed. One of `TagVocabularyTargets.all`.
     *
     * Clamped here as well as on the server, and the two are not the same check: the server's
     * is the rule and answers a sentence, this one keeps a control that cannot express an
     * out-of-range value from sending one and being told off for it. A platform that *can*
     * express one — a text field, a scripted client — still meets the server's.
     *
     * Clamped to the *range* rather than snapped to the nearest preset, for the reason the
     * store gives: the five presets are a settings window's buttons, not a claim that 300 is
     * a meaningless ceiling.
     */
    fun onTagVocabularyTargetChosen(target: Int) {
        change {
            it.copy(
                aiTagVocabularyTarget = target.coerceIn(
                    TagVocabularyTargets.NO_LIMIT,
                    TagVocabularyTargets.MAX,
                ),
            )
        }
    }

    /** A segment of *Summary length* was pressed. One of `SummaryLengths.all`. */
    fun onSummaryLengthChosen(length: String) {
        change { it.copy(aiSummaryLength = length) }
    }

    /** *Language* was chosen. One of `SummaryLanguages.all`. */
    fun onLanguageChosen(language: String) {
        change { it.copy(aiLanguage = language) }
    }

    // An `onModelChosen` was here, behind the one text field on the settings window. Both
    // are gone with `AccountSettings.aiModel` — which model the operator's key is spent on
    // is a fact about the deployment, like the key itself, and not this account's to pick.
    // It also means nothing on this screen is free text any more, which is why every intent
    // above takes a value from a fixed set.

    // ── The two tag lists, which are one control with two states ─────────────
    //
    // A quick tag is a chip the save popup offers; a default tag is a chip it offers with
    // the chip already pressed. So **every default tag is a quick tag**, and these four
    // intents are what keeps that true from this side — the store enforces it again on the
    // way in (see `AccountSettingsStore.put`), and the enforcement is in both places on
    // purpose: the server's is the rule, and this one is what stops a chip appearing,
    // disappearing and reappearing while a write is in flight.
    //
    // The asymmetry between the two removals is the same one SheetMark's pair settled on
    // and is worth stating rather than deriving: taking a tag out of *offered* has to take
    // it out of *offered and pressed*, or the invariant breaks; taking it out of *pressed*
    // leaves it offered, because "stop putting this on everything, but keep it one press
    // away" is the commoner intent by a distance.

    /**
     * *Offer by default* was edited, as a whole list.
     *
     * ── Why a whole list rather than an add and a remove ─────────────────────
     *
     * Four intents were here — `onQuickTagAdded`, `onQuickTagRemoved` and the two beside
     * them — behind a field that turned each committed tag into a chip. They are one intent
     * each now, and the reason is that **the order became load-bearing**. The list window's
     * tag rail draws these in the order they are given, so the account's order is a thing
     * the account has to be able to *choose*; an add-and-remove pair can only ever append,
     * and the old ones sorted on top of that, so the answer to "put `#read-later` first" was
     * "call it something earlier in the alphabet".
     *
     * A list arrives here already parsed by whatever field raised it — see the web view's
     * `TagListField` — and everything about the shape of that field stays there. What this
     * owns is the pair's one rule, below.
     *
     * ── Normalised, and in the order it was written ──────────────────────────
     *
     * Lower-cased and de-duplicated the way the server will do it, so the field does not
     * rearrange itself a moment later when the answer lands, but **not sorted**: that is the
     * whole point. The server is still the authority — `normaliseTagsKeepingOrder` is where
     * the rule really lives — and this only keeps the two from disagreeing for a second.
     *
     * Taking a tag out of *offered* takes it out of *applied* too, which is the asymmetry the
     * two lists have always had: a default tag that is not offered would be put on every save
     * with no chip to turn it off. The other direction does not hold, and [onDefaultTagsEdited]
     * says why.
     */
    fun onQuickTagsEdited(tags: List<String>) {
        val clean = tags.normalisedInOrder()
        change { current ->
            current.copy(
                saveQuickTags = clean,
                // Whatever is no longer offered cannot still be applied. Filtered rather than
                // reordered, so the applied list keeps its own arrangement.
                saveDefaultTags = current.saveDefaultTags.filter { it in clean },
            )
        }
    }

    /**
     * *Apply by default* was edited, as a whole list.
     *
     * Joins both lists, for the invariant above: a tag put on every save has to be one you
     * can un-press for the one save you did not want it on. Appended rather than merged in
     * place, so a tag that was already offered keeps the position the account gave it and
     * only a genuinely new one arrives at the end.
     *
     * Emptying it is allowed, and it does not empty the other: this is somebody saying *stop
     * putting these on everything*, not *stop offering them*.
     */
    fun onDefaultTagsEdited(tags: List<String>) {
        val clean = tags.normalisedInOrder()
        change { current ->
            current.copy(
                saveQuickTags = (current.saveQuickTags + clean).distinct(),
                saveDefaultTags = clean,
            )
        }
    }

    /**
     * Lower-cased, `#`-prefixed, de-duplicated — **and left in the order it came in**.
     *
     * The client half of `normaliseTagsKeepingOrder`, and a small copy of it for the same
     * reason `hashPrefixedTag` is a copy of `hashPrefixed`: the server re-normalises
     * everything it is sent and remains the authority, and this exists so the field reads the
     * way the stored list will rather than flickering when the write comes back.
     */
    private fun List<String>.normalisedInOrder(): List<String> = this
        .map { it.trim().trimStart('#').trim().lowercase() }
        .filter { it.isNotEmpty() }
        .map { "#$it" }
        .distinct()

    // Two more intents were here — *Close the popup after saving* and *Warn when a URL is
    // already saved* — and they are gone with the fields behind them. Both wrote a boolean
    // the server stored and nothing read: the popup has always closed itself on a timer,
    // and a re-save now says so in its own sentence rather than on request. See
    // `AccountSettings`, and 3.sqm for the columns.

    /**
     * Apply [transform] to the form and write the result.
     *
     * The single funnel every control goes through, which is what makes "a change is
     * written" one claim to check rather than nine. A no-op transform still writes: a
     * platform that raised an intent for a value that happens to equal the current one has
     * told us nothing about whether the server agrees, and a write of the whole set is
     * idempotent by construction.
     */
    private fun change(transform: (AccountSettings) -> AccountSettings) {
        val ready = _stateFlow.value.ready ?: return
        val updated = transform(ready.settings)
        // Published before the request rather than after it. The person moved a control and
        // the control has to move — a screen that waited for a round trip before showing the
        // new position would flicker back on every flick over a slow connection.
        _stateFlow.value = State(
            ready = ready.copy(settings = updated, isSaving = true, message = null),
        )
        scope.launch { persist() }
    }

    /**
     * Write the form as it stands, under the lock.
     *
     * **The form is read *inside* the lock**, not captured before it, which is the whole
     * point of the arrangement: two flicks in a row queue two writes, the second reads the
     * newer form, and whichever order they happened to be launched in the last one to run
     * sends the value that is on screen.
     */
    private suspend fun persist() {
        writes.withLock {
            val ready = _stateFlow.value.ready ?: return
            try {
                val stored = storage.setAccountSettings(ready.settings.asRequest())
                // The server's answer, not the request: it trims and normalises tags, so
                // painting what was sent would leave the screen quietly disagreeing with
                // what is held. Anything typed *since* this write started is not lost —
                // it is already in a later write queued behind this lock.
                publishStored(stored)
                // ── And tell this client's own windows ───────────────────────
                //
                // `ServerEvent.AccountSettingsChanged` will come back over the channel and be
                // **dropped**, correctly: `ServerEventStream` filters events carrying this
                // client's own origin, or a write would fight whatever the person is still
                // doing. But an origin is one client *instance* — one browser tab — and the
                // settings window and the list window are two windows inside it. So the echo
                // filter, which exists to stop a client reacting to itself, also stopped the
                // list window next door from ever hearing about a nomination made here.
                //
                // That was the whole of the original bug: changing the tag lists reordered
                // nothing until the list window was closed and opened again. The channel
                // fixed it between devices and could not fix it within one.
                //
                // Said locally rather than by loosening the filter, and through the same
                // `accountSettingsRevisionFlow` the remote case already travels, so there is
                // one path into a rail redraw instead of two. Safe where re-reading geometry
                // would not be: nothing in a list window is mid-gesture because of a control
                // pressed in another window, and the reader of this is a re-fetch that
                // repaints chips.
                storage.onAccountSettingsChanged()
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (failure: Exception) {
                // The server's own sentence, which names the field and says what it will
                // take. Replacing it with a generic failure would throw away the only part
                // that says what to change.
                publishMessage(failure.readableMessage(COULD_NOT_SAVE))
            }
        }
    }

    // ── The backfill ─────────────────────────────────────────────────────────

    /**
     * *Summarise N older bookmarks* was pressed.
     *
     * Queues every bookmark that has never been tried, then reads the queue back so the panel
     * is right immediately — [State.Ready.awaitingSummary] is what the label is built from and
     * [State.Ready.isBackfillRunning] is what decides whether the panel offers a *Cancel*, and
     * both of them have just changed. The poll would fix it within two seconds anyway; what
     * this buys is that the button somebody has just pressed does not spend those two seconds
     * offering to queue four hundred bookmarks it has already queued.
     *
     * One reading rather than two, and the two numbers swapping together is the point: they
     * come out of one response, so there is no instant at which the panel says both "nothing
     * is waiting" and "nothing is running".
     *
     * Refused by the server with a sentence when this deployment has no key (503) or the
     * account has both AI switches off (409), and that sentence is what is shown. The button
     * is **not** hidden in either case: a control that explains itself when pressed is worth
     * more than one that silently is not there, which is the same judgement the edit
     * window's *Regenerate* makes.
     */
    fun onBackfillRequested() {
        val ready = _stateFlow.value.ready ?: return
        if (ready.isBackfilling) return
        publish(ready.copy(isBackfilling = true, backfillMessage = null))
        scope.launch {
            try {
                val queued = storage.summariseMissing().enqueued
                val refreshed = progressOrEmpty()
                _stateFlow.value.ready?.let {
                    publish(
                        it.copy(
                            progress = refreshed,
                            isBackfilling = false,
                            backfillMessage = backfillSentence(queued),
                        ),
                    )
                }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (failure: Exception) {
                _stateFlow.value.ready?.let {
                    publish(
                        it.copy(
                            isBackfilling = false,
                            backfillMessage = failure.readableMessage(COULD_NOT_BACKFILL),
                        ),
                    )
                }
            }
        }
    }

    /**
     * *Cancel* was pressed.
     *
     * Empties the queue and reads it straight back, for [onBackfillRequested]'s reason: the
     * panel has to swap back to its idle half now rather than in two seconds, and the count
     * it swaps to — the cancelled rows are eligible for a backfill again — arrives in the
     * same response as the emptied queue.
     *
     * **No confirmation is asked for anywhere, and none should be added.** Cancelling
     * destroys nothing: the rows go back to untried, so pressing *Backfill* again resumes
     * from where this stopped. A dialog in front of a reversible button is a dialog people
     * learn to dismiss without reading, and the next one is the one that mattered.
     *
     * Guarded against a second press while the first is in flight, exactly as the backfill
     * is. What it is deliberately *not* guarded by is whether this client believes a backfill
     * is running: the panel offers this because the counts say the queue is not empty, and if
     * it drained in the meantime the server answers zero and the next poll draws an idle
     * panel — which is what happened anyway.
     */
    fun onCancelBackfillRequested() {
        val ready = _stateFlow.value.ready ?: return
        if (ready.isCancelling) return
        publish(ready.copy(isCancelling = true, backfillMessage = null))
        scope.launch {
            try {
                val cancelled = storage.cancelBackfill().cancelled
                val refreshed = progressOrEmpty()
                _stateFlow.value.ready?.let {
                    publish(
                        it.copy(
                            progress = refreshed,
                            isCancelling = false,
                            backfillMessage = cancelSentence(cancelled),
                        ),
                    )
                }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (failure: Exception) {
                _stateFlow.value.ready?.let {
                    publish(
                        it.copy(
                            isCancelling = false,
                            backfillMessage = failure.readableMessage(COULD_NOT_CANCEL),
                        ),
                    )
                }
            }
        }
    }

    /**
     * What a finished backfill says.
     *
     * Zero is an ordinary answer rather than a failure — every bookmark was already
     * summarised, already queued, or already tried and given up on — and saying so in words
     * is the alternative to a spinner over work that is not happening.
     */
    private fun backfillSentence(queued: Long): String = when (queued) {
        0L -> "Nothing to do — every bookmark has already been tried."
        1L -> "One bookmark is in the queue. It runs in the background."
        else -> "$queued bookmarks are in the queue. They run in the background, " +
            "roughly twenty a minute."
    }

    /**
     * What a finished cancel says.
     *
     * It says the work can be resumed, because that is the thing somebody cannot see: the
     * rows went back to untried rather than being marked as refused, so the backfill button
     * is about to offer them again and pressing it carries on from here. Without that
     * sentence, *Cancel* reads as a decision that has to be undone by hand.
     *
     * Zero is an ordinary answer — the queue drained between the panel drawing a *Cancel* and
     * the press landing — and it says so rather than claiming to have stopped something.
     */
    private fun cancelSentence(cancelled: Long): String = when (cancelled) {
        0L -> "There was nothing left in the queue."
        1L -> "One bookmark taken out of the queue. Press Backfill to pick it up again."
        else -> "$cancelled bookmarks taken out of the queue. Press Backfill to pick them " +
            "up again. Anything already being read will finish."
    }

    // ── Tidying the vocabulary ───────────────────────────────────────────────
    //
    // The largest thing on this window, and the one place a press can remove several thousand
    // tag rows. Three rules shape everything below and each of them is a decision rather than
    // a convention:
    //
    //  1. **The dialog does not open until the plan is ready.** A modal containing a spinner
    //     is a modal somebody has to dismiss to escape from work they cannot see; the button
    //     says *Planning…* and stays where it is instead.
    //
    //     That was written when the wait was a second or two, and it is now about ninety on a
    //     large collection: the model that plans a tidy reasons over the entire surviving
    //     vocabulary before it answers. The rule survives the change — a modal is *more*
    //     objectionable over a ninety-second wait, not less — but a disabled button on its own
    //     no longer carries it, which is what `tidyPlanningSeconds` and `tidyProgressNote` are
    //     for. Nothing streams, so elapsed time is the only honest progress there is.
    //  2. **The ticks live here, not in the dialog.** What is selected is state a phone would
    //     need too, and the selection is what every count is derived from — so a view that
    //     held it would be a view deriving numbers, which is exactly what §05 of the
    //     architecture note puts on this side.
    //  3. **Every count comes from the server.** Unticking a row asks again rather than
    //     subtracting, because the number that matters — how many bookmarks would be left with
    //     no tags at all — needs every bookmark's tag rows. See `TidyImpact`.
    //
    // There is deliberately **no confirmation in front of Apply**. The dialog has already
    // shown a preview computed from these exact numbers, and [onUndoTidyRequested] is one
    // press away; a dialog in front of a previewed, reversible action is one people learn to
    // dismiss without reading, and the next one is the one that mattered.

    /**
     * *Tidy…* was pressed.
     *
     * Plans, then opens the dialog with the server's pre-ticked rows already selected. A
     * failure leaves the dialog shut and puts the server's sentence on the panel — including
     * the 429 that says the planning button has been pressed enough for now, which is the one
     * refusal a person can act on by waiting.
     *
     * Guarded against a second press while the first is in flight, exactly as the backfill is.
     */
    fun onTidyRequested() {
        val ready = _stateFlow.value.ready ?: return
        if (ready.isPlanningTidy) return
        publish(ready.copy(isPlanningTidy = true, tidyPlanningSeconds = 0, tidyMessage = null))
        // A second coroutine rather than a field the view polls, so the elapsed count arrives
        // through the one state flow everything else on this screen is drawn from. It stops
        // itself at the point the server would have given up anyway — see the tidy timeout on
        // the provider — because a counter still climbing past a request that can no longer
        // succeed is telling somebody to keep waiting for nothing.
        val ticker = scope.launch {
            for (second in 1..LONGEST_TIDY_WAIT_SECONDS) {
                delay(1_000)
                val waiting = _stateFlow.value.ready?.takeIf { it.isPlanningTidy } ?: return@launch
                publish(waiting.copy(tidyPlanningSeconds = second))
            }
        }
        scope.launch {
            try {
                val plan = storage.planTidy()
                _stateFlow.value.ready?.let {
                    publish(
                        it.copy(
                            isPlanningTidy = false,
                            // Nothing to do is said in words rather than by opening an empty
                            // dialog: a modal listing no changes is a modal whose only
                            // affordance is Cancel.
                            tidyMessage = if (plan.isEmpty) NOTHING_TO_TIDY else null,
                            tidyDialog = if (plan.isEmpty) null else State.TidyDialog(plan),
                        ),
                    )
                }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (failure: Exception) {
                _stateFlow.value.ready?.let {
                    publish(
                        it.copy(
                            isPlanningTidy = false,
                            tidyMessage = failure.readableMessage(COULD_NOT_PLAN),
                        ),
                    )
                }
            } finally {
                // In a `finally` rather than after each branch, so the counter cannot outlive
                // the request in the one case nobody writes a test for: this view model's scope
                // being cancelled while a plan is in flight.
                ticker.cancel()
            }
        }
    }

    /** A merge row was ticked or unticked. Recounts. */
    fun onTidyMergeTicked(from: String, to: String, ticked: Boolean) {
        retick { dialog ->
            val pair = TidyPair(from = from, to = to)
            dialog.copy(
                tickedMerges = if (ticked) dialog.tickedMerges + pair else dialog.tickedMerges - pair,
            )
        }
    }

    /** A drop row was ticked or unticked. Recounts. */
    fun onTidyDropTicked(tag: String, ticked: Boolean) {
        retick { dialog ->
            dialog.copy(
                tickedDrops = if (ticked) dialog.tickedDrops + tag else dialog.tickedDrops - tag,
            )
        }
    }

    /**
     * Apply [transform] to the open dialog and ask the server what the result would cost.
     *
     * The single funnel both tick intents go through, which is what makes "a change to the
     * selection is recounted" one claim rather than two. The new selection is published
     * immediately — a tick has to move when it is pressed — and the number under it is marked
     * as being recounted rather than left showing a total that no longer describes it.
     */
    private fun retick(transform: (State.TidyDialog) -> State.TidyDialog) {
        val ready = _stateFlow.value.ready ?: return
        val dialog = ready.tidyDialog ?: return
        publish(ready.copy(tidyDialog = transform(dialog).copy(isCounting = true)))
        scope.launch { recount() }
    }

    /**
     * Ask what the current selection would cost, under the lock.
     *
     * **The selection is read *inside* the lock**, not captured before it — the arrangement
     * [persist] uses and for its reason exactly: three ticks in quick succession queue three
     * counts, and whichever order they run in the last one describes what is on screen. Without
     * it a slow first request could land after a fast third and paint a total for a selection
     * the person had already moved on from.
     *
     * A failure leaves the last good number where it is and says so on the dialog. Blanking it
     * would be worse: a summary that vanishes because one request in a series did not answer
     * reads as the plan having become unknowable.
     */
    private suspend fun recount() {
        tidyCounts.withLock {
            val dialog = _stateFlow.value.ready?.tidyDialog ?: return
            val counted = try {
                storage.previewTidy(dialog.selection)
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (failure: Exception) {
                _stateFlow.value.ready?.let { ready ->
                    ready.tidyDialog?.let {
                        publish(
                            ready.copy(
                                tidyDialog = it.copy(
                                    isCounting = false,
                                    message = failure.readableMessage(COULD_NOT_COUNT),
                                ),
                            ),
                        )
                    }
                }
                return
            }
            _stateFlow.value.ready?.let { ready ->
                ready.tidyDialog?.let {
                    publish(
                        ready.copy(
                            tidyDialog = it.copy(
                                impact = counted,
                                isCounting = false,
                                message = null,
                            ),
                        ),
                    )
                }
            }
        }
    }

    /** *Cancel*, `Esc`, or the backdrop. Nothing was applied and nothing is remembered. */
    fun onTidyDismissed() {
        _stateFlow.value.ready?.let { publish(it.copy(tidyDialog = null)) }
    }

    /**
     * *Apply* was pressed.
     *
     * Closes the dialog on success and re-reads the **three** things it changed. The first two
     * are obvious: the panel's counts, and the account's tag vocabulary, which the `#tag`
     * menus on this very window draw from.
     *
     * The third is the one that is easy to miss and was: **the account's own settings**.
     * Applying a tidy sweeps `saveQuickTags` and `saveDefaultTags` server-side, because they
     * hold raw tag strings — so a window that did not re-read them went on showing
     * *Offer by default: #election* over a stored `#elections`, and the next edit to either
     * field would have written the stale spelling back and recreated the tag the tidy had just
     * removed. Nothing on screen said anything was wrong.
     *
     * The sentence that replaces the dialog says what happened *and* that it can be undone,
     * because that is the fact somebody cannot see: the *Undo last tidy* the panel is about to
     * grow is the only thing standing between them and a change they cannot reverse.
     *
     * **This client's own windows are told locally**, exactly as [persist] tells them about a
     * settings write and for the same reason: the server broadcasts a change carrying this
     * client's origin, `ServerEventStream` correctly drops the echo, and a list window in the
     * same browser tab would otherwise go on drawing tags that no longer exist until it was
     * closed and reopened.
     */
    fun onTidyApplyRequested() {
        val ready = _stateFlow.value.ready ?: return
        val dialog = ready.tidyDialog ?: return
        if (dialog.isApplying) return
        publish(ready.copy(tidyDialog = dialog.copy(isApplying = true, message = null)))
        val selection = dialog.selection
        scope.launch {
            try {
                val result = storage.applyTidy(selection)
                storage.onBookmarksChanged(emptyList())
                val swept = settingsOrKeep()
                _stateFlow.value.ready?.let {
                    publish(
                        it.copy(
                            settings = swept ?: it.settings,
                            tidyDialog = null,
                            tidy = tidyStatusOrEmpty(),
                            knownTags = knownTags(),
                            tidyMessage = tidySentence(result),
                        ),
                    )
                }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (failure: Exception) {
                // The dialog stays open, holding the selection, so the sentence is beside the
                // rows it is about — the same judgement the file header makes about a refused
                // settings write.
                _stateFlow.value.ready?.let { current ->
                    current.tidyDialog?.let {
                        publish(
                            current.copy(
                                tidyDialog = it.copy(
                                    isApplying = false,
                                    message = failure.readableMessage(COULD_NOT_TIDY),
                                ),
                            ),
                        )
                    }
                }
            }
        }
    }

    /**
     * *Undo last tidy* was pressed.
     *
     * One step back, and the snapshot is spent by taking it — so the button disappears
     * afterwards, which is what `TidyStatus.canUndo` re-read below is for. No confirmation, for
     * the cancel button's reason: this is the thing that undoes a destructive act, and a
     * dialog in front of the escape hatch is the wrong place to slow somebody down.
     */
    fun onUndoTidyRequested() {
        val ready = _stateFlow.value.ready ?: return
        if (ready.isUndoingTidy) return
        publish(ready.copy(isUndoingTidy = true, tidyMessage = null))
        scope.launch {
            try {
                val undone = storage.undoTidy()
                storage.onBookmarksChanged(emptyList())
                val restored = settingsOrKeep()
                _stateFlow.value.ready?.let {
                    publish(
                        it.copy(
                            settings = restored ?: it.settings,
                            isUndoingTidy = false,
                            tidy = tidyStatusOrEmpty(),
                            knownTags = knownTags(),
                            tidyMessage = undoSentence(undone),
                        ),
                    )
                }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (failure: Exception) {
                _stateFlow.value.ready?.let {
                    publish(
                        it.copy(
                            isUndoingTidy = false,
                            tidyMessage = failure.readableMessage(COULD_NOT_UNDO),
                        ),
                    )
                }
            }
        }
    }

    /**
     * The account's settings as the server now holds them, or null when it would not say.
     *
     * Read after a tidy and after an undo, because both rewrite `saveQuickTags` and
     * `saveDefaultTags` on the server — see [onTidyApplyRequested], where the bug this
     * prevents is described.
     *
     * **Null keeps what is on screen** rather than falling back to a default, which is the
     * difference between this and [tidyStatusOrEmpty] beside it: an empty status draws a panel
     * that says nothing, where empty *settings* would draw two tag fields somebody had filled
     * in as though they had been cleared — and the next edit would write that back.
     */
    private suspend fun settingsOrKeep(): AccountSettings? =
        try {
            storage.accountSettings()
        } catch (cancellation: CancellationException) {
            throw cancellation
        } catch (_: Exception) {
            null
        }

    /**
     * What a finished tidy says.
     *
     * It names the undo, which is the part nobody can see. Without that sentence, *Apply*
     * reads as a decision that has to be reversed by hand — and the whole argument for not
     * putting a confirmation in front of it is that it does not.
     */
    private fun tidySentence(result: TidyResult): String {
        val impact = result.applied
        val head = "${impact.tagsBefore} tags are now ${impact.tagsAfter}."
        val orphans = when (impact.orphaned) {
            0L -> "Every bookmark still has a tag."
            1L -> "One bookmark was left with no tags."
            else -> "${impact.orphaned} bookmarks were left with no tags."
        }
        return if (result.canUndo) "$head $orphans Undo puts it all back." else "$head $orphans"
    }

    /**
     * What a finished undo says.
     *
     * Zero is an ordinary answer — the snapshot was already spent, or there never was one —
     * and it says so rather than claiming to have restored something.
     */
    private fun undoSentence(result: UndoTidyResult): String = when (result.restored) {
        0L -> "There was nothing to undo."
        else -> "${result.restored} tag rows restored. You have ${result.tags} tags again."
    }

    // ── Agent access, on the Connections tab ─────────────────────────────────
    //
    // Two intents, and pointedly **not** routed through [change]. That funnel writes the whole
    // of `AccountSettings` in one `PUT`, and this flag is deliberately not in it: it lives on
    // the `users` row rather than in `user_settings` precisely so that the settings form's
    // save path cannot switch agent access on as a side effect of somebody changing their
    // summary language. Sharing the funnel here would put that back by another door.
    //
    // Both write and then paint the server's answer, which carries the whole state — so a
    // revoke that also happened to change the switch, or a switch flicked while a second
    // window revoked something, leaves one coherent picture rather than two halves.

    /**
     * *Let AI agents act on your bookmarks* was flicked.
     *
     * **No confirmation on the way off**, unlike Lunicle's equivalent, and the reason is the
     * same one this window uses to refuse a confirmation in front of *Apply*: the act is
     * reversible and costs nothing to undo. Off revokes nothing — both server gates re-read
     * the flag per request, so connected agents stop at their next call and work again the
     * instant it goes back on, with no reconnect. The sentence under the switch is where that
     * is said, because a dialog saying it would be a dialog people learn to dismiss.
     */
    fun onAgentAccessToggled(enabled: Boolean) {
        val ready = _stateFlow.value.ready ?: return
        if (ready.isMcpBusy || ready.mcp == null) return
        publish(ready.copy(isMcpBusy = true, mcpMessage = null))
        scope.launch {
            try {
                val landed = storage.setMcpEnabled(enabled).asMcp()
                _stateFlow.value.ready?.let {
                    publish(it.copy(mcp = landed, isMcpBusy = false))
                }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (failure: Exception) {
                _stateFlow.value.ready?.let {
                    publish(
                        it.copy(
                            isMcpBusy = false,
                            mcpMessage = failure.readableMessage(COULD_NOT_SET_AGENT_ACCESS),
                        ),
                    )
                }
            }
        }
    }

    /**
     * *Revoke* was pressed on one connected agent.
     *
     * **No confirmation, deliberately — and this one is irreversible**, which is the opposite
     * of the argument above. It stands anyway for two reasons the switch does not have: the
     * press is *unambiguous* — the row names the agent, the dates say when it last acted, and
     * the button is on that row — and the recovery is to connect it again, which is the two
     * lines the same pane is showing. A confirmation over a press whose target is named on the
     * button's own row is the sort people click through without reading.
     */
    fun onRevokeConnectionRequested(clientId: String) {
        val ready = _stateFlow.value.ready ?: return
        if (ready.isMcpBusy || ready.mcp == null) return
        publish(ready.copy(isMcpBusy = true, mcpMessage = null))
        scope.launch {
            try {
                val remaining = storage.revokeMcpConnection(clientId).asMcp()
                _stateFlow.value.ready?.let {
                    publish(it.copy(mcp = remaining, isMcpBusy = false))
                }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (failure: Exception) {
                _stateFlow.value.ready?.let {
                    publish(
                        it.copy(
                            isMcpBusy = false,
                            mcpMessage = failure.readableMessage(COULD_NOT_REVOKE),
                        ),
                    )
                }
            }
        }
    }

    // ── Testing the connection ───────────────────────────────────────────────

    /**
     * *Test connection* was pressed.
     *
     * Makes the server make one real call, and reports what came back. This is the question
     * the pill cannot answer: `aiAvailable` says a key is set, and a revoked key, a typo, an
     * exhausted balance and a build asking for a model that has been retired all look
     * exactly like a working deployment from there.
     *
     * **A provider that refused is a completed test**, not a failure — see
     * [se.soderbjorn.lunapin.clientserver.AiTestResult], where that shape is argued. What
     * lands in [State.Test.failure] instead is the states where no call was made at all: no
     * key on this deployment, or the throttle saying it has been pressed enough for now.
     */
    fun onTestConnectionRequested() {
        val ready = _stateFlow.value.ready ?: return
        if (ready.test?.isRunning == true) return
        publish(ready.copy(test = State.Test(isRunning = true)))
        scope.launch {
            val outcome = try {
                val result = storage.testAi()
                State.Test(ok = result.ok, message = result.message)
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (failure: Exception) {
                State.Test(ok = false, message = failure.readableMessage(COULD_NOT_TEST))
            }
            _stateFlow.value.ready?.let { publish(it.copy(test = outcome)) }
        }
    }

    // ── Internals ────────────────────────────────────────────────────────────

    /** Publish a form state, unless the window has left the form behind. */
    private fun publish(ready: State.Ready) {
        if (_stateFlow.value.ready == null) return
        _stateFlow.value = State(ready = ready)
    }

    /** The server's answer became the form, and nothing is being written any more. */
    private fun publishStored(stored: AccountSettings) {
        _stateFlow.value.ready?.let {
            publish(it.copy(settings = stored, isSaving = false, message = null))
        }
    }

    /** A write was refused. The form keeps what was chosen; see the file header. */
    private fun publishMessage(message: String) {
        _stateFlow.value.ready?.let { publish(it.copy(isSaving = false, message = message)) }
    }

    /**
     * Exactly one field is non-null.
     *
     * The nullable-payload shape from §06 rather than a sealed hierarchy, so a Swift view
     * reads it as an `if let` chain rather than a cast per branch.
     *
     * @property loading the first state of the window, before anything has answered.
     * @property ready the screen.
     * @property failed the settings could not be read, with a sentence to show. Only the
     *   settings can produce this — see [load].
     */
    data class State(
        val loading: Loading? = null,
        val ready: Ready? = null,
        val failed: Failed? = null,
    ) {
        data object Loading

        /**
         * The screen, and everything on it.
         *
         * @property settings the form **and** the baseline, which are one object here
         *   because there is no Save separating them: a control moves, the value changes,
         *   and a write follows. `AccountSettings.aiAvailable` rides along inside it and is
         *   the only thing this screen is ever told about the key.
         * @property usage this month's tally. Fetched once per window, on the load, and not
         *   polled: it is the account's own numbers rather than the queue's, and nothing on
         *   this window moves while somebody watches it. The one number the backfill panel
         *   needs is on [progress] instead — [AiUsage.awaitingSummary] answers the same
         *   question from the same server-side count, but from a response that is two seconds
         *   old at most and that carries the queue's counts alongside it, so the panel's two
         *   halves cannot disagree about which state it is in.
         * @property progress how far the enrichment queue has got, re-read every
         *   [PROGRESS_POLL_MILLIS] for as long as the window is open. **This is what says a
         *   backfill is running** — see [EnrichmentProgress], where the case against a client
         *   flag is made. Zeroes when the poll has not answered, which draws as an idle panel.
         * @property knownTags the account's vocabulary with counts, for the *Apply by default*
         *   menu. Empty when the tag route did not answer, which costs the menu its rows and
         *   nothing else.
         * @property isSaving a write is in flight. **Not a reason to freeze the controls**,
         *   unlike the edit window's flag of the same name: there is no form being submitted
         *   here, only a value that has already changed, and a screen that went inert after
         *   every flick would be unusable. It is a quiet indicator and nothing more.
         * @property message the server's own sentence about the last refused write, or null.
         *   Shown over the form the person is still holding, because the value it is about is
         *   still on screen and still theirs to fix.
         * @property isBackfilling the *enqueue request* is in flight, and nothing more than
         *   that — it says the button has been pressed and the POST has not answered, which
         *   is a second or two. It emphatically does **not** mean a backfill is happening;
         *   that is [isBackfillRunning], counted by the server. The two were one flag once,
         *   and the bug was exactly this confusion: the panel called itself done the moment
         *   the request returned, before a single page had been read.
         * @property isCancelling the cancel request is in flight. Its own flag rather than
         *   sharing [isBackfilling]: the two are opposite intents, and one flag would mean the
         *   panel could not say which of them somebody had just pressed.
         * @property backfillMessage what the last backfill or cancel did, or why it was
         *   refused. One field for both because the panel has one place to say it and the two
         *   press the same spot — a second line would be a stale sentence about the backfill
         *   sitting under a fresh one about the cancel that stopped it.
         * @property test the last connection test, or null if none has been asked for. Its own
         *   payload rather than three fields here, because "not tested" is a state the screen
         *   draws differently from "tested and it worked".
         * @property tidy how large the vocabulary is and whether the last tidy can be undone.
         *   Fetched with the settings and re-read after every tidy and every undo, because
         *   both change all four of its numbers. Not polled: unlike the enrichment queue,
         *   nothing here moves unless somebody presses something on this window.
         * @property isPlanningTidy *Tidy…* has been pressed and the plan has not arrived. The
         *   button says so and refuses a second press — planning can spend the operator's key,
         *   and a second press would buy a second answer to the question already being asked.
         * @property tidyPlanningSeconds how long that press has been waiting, in whole seconds,
         *   counted from zero and meaningless unless [isPlanningTidy]. Here rather than in a
         *   view for §05's reason — a phone would need the same number — and it exists at all
         *   because the wait stopped being a second or two: the model that plans a tidy reasons
         *   over the whole vocabulary and takes around a minute and a half over a large one. A
         *   disabled button reading *Planning…* is indistinguishable from a hung one at that
         *   length, and the difference between the two is the whole reason somebody waits
         *   rather than reloading the page. See [tidyProgressNote].
         * @property isUndoingTidy the undo request is in flight. Its own flag rather than
         *   sharing [isPlanningTidy], for [isCancelling]'s reason: the two are opposite intents
         *   and one flag would leave the panel unable to say which was pressed.
         * @property tidyMessage what the last tidy, undo or planning attempt came to, or why it
         *   was refused. One field for all three because the panel has one place to say it and
         *   they press the same spot.
         * @property tidyDialog the open plan, or null when there is none. **Null until the plan
         *   is ready** — the dialog is not opened over a spinner; see the section above the
         *   intents, where that is argued.
         * @property mcp the Connections tab, or **null when the server would not say**. Not an
         *   empty [Mcp] for that case — see [mcpOrNull]: everything else on this screen falls
         *   back to zeroes, and this one must not, because *off with nothing connected* is a
         *   claim about a security gate rather than a count.
         * @property isMcpBusy a switch or a revoke is in flight. One flag for both, unlike the
         *   backfill's pair, because they are the same claim: the pane is showing a state the
         *   server is in the middle of changing, and every control on it should wait for the
         *   answer rather than queue a second write against a list that is about to move.
         * @property mcpMessage why the last switch or revoke was refused, or null. Its own line
         *   rather than [message], for [tidyMessage]'s reason: it belongs beside the controls it
         *   is about, on a tab the person is looking at.
         */
        data class Ready(
            val settings: AccountSettings,
            val usage: AiUsage = AiUsage(),
            val progress: EnrichmentProgress = EnrichmentProgress(),
            val knownTags: List<TagUse> = emptyList(),
            val isSaving: Boolean = false,
            val message: String? = null,
            val isBackfilling: Boolean = false,
            val isCancelling: Boolean = false,
            val backfillMessage: String? = null,
            val test: Test? = null,
            val tidy: TidyStatus = TidyStatus(),
            val isPlanningTidy: Boolean = false,
            val tidyPlanningSeconds: Int = 0,
            val isUndoingTidy: Boolean = false,
            val tidyMessage: String? = null,
            val tidyDialog: TidyDialog? = null,
            val mcp: Mcp? = null,
            val isMcpBusy: Boolean = false,
            val mcpMessage: String? = null,
        ) {
            /**
             * Whether this deployment has a model provider key at all.
             *
             * The pill, and the *only* thing anything here knows about the key: not its
             * value, not a prefix, not its length. See `AiConfig` on the server, which is
             * where that is decided and where it is the whole of what leaves.
             */
            val isAiAvailable: Boolean get() = settings.aiAvailable

            /**
             * What to say under the button while a plan is being made, or null when none is.
             *
             * ── Three sentences, because a wait this long has three stages ───
             *
             * The tidy model reads the whole surviving vocabulary and reasons over it before it
             * writes anything, which measured at about ninety seconds on a twelve-hundred-tag
             * collection. Nothing streams back in the meantime — there is one request and one
             * answer — so the only honest progress available is elapsed time, and the job of
             * these lines is to make a wait that *is* fine look like one.
             *
             * They change as they do because a line that never changes reads as a frozen page
             * however truthful it is. The first says what is happening, the second says the
             * silence is expected, and the third gives the number somebody needs in order to
             * decide whether to keep waiting — which is the point at which they would otherwise
             * reload and lose the request that was already paid for.
             *
             * A count of tags rather than a percentage, because there is no percentage to be
             * had and inventing a bar that fills at a guessed rate would be a lie that gets
             * caught: it would sit at 90% for the last forty seconds every time.
             */
            val tidyProgressNote: String? get() = when {
                !isPlanningTidy -> null
                tidyPlanningSeconds < 8 -> "Reading ${tidy.tags} tags…"
                tidyPlanningSeconds < 30 ->
                    "Working out which of them are the same idea. This is the slow part."
                else -> "Still going — ${tidyPlanningSeconds}s so far. A large collection " +
                    "takes a minute or two."
            }

            /**
             * How many bookmarks a backfill would pick up right now.
             *
             * What the button is labelled with. Zero means there is nothing to do, and a
             * platform is expected to say so and disable it rather than offer a no-op.
             *
             * Read off [progress] rather than [usage] because it has to stay true while
             * somebody watches: a backfill that finishes, or a cancel made on another device,
             * changes this number with nothing pressed here. See [usage].
             */
            val awaitingSummary: Long get() = progress.awaitingSummary

            /**
             * Whether a backfill is happening, **counted rather than remembered**.
             *
             * The panel swaps on this: idle offers *Summarise N older bookmarks*, running
             * offers *Cancel*. Because it is derived from the queue's own counts it is right
             * for a window opened after the backfill started, for a client that has been
             * reloaded since, and for a backfill started on a phone — none of which a flag set
             * when this client pressed a button could know anything about.
             */
            val isBackfillRunning: Boolean get() = progress.isRunning

            /** How many bookmarks are still to be read. The number the panel counts down. */
            val bookmarksLeft: Long get() = progress.left

            /**
             * How many enrichments this account has given up on.
             *
             * Shown beside [bookmarksLeft] and not acted on: `enqueueMissingForAi` will not
             * pick up a row that spent its three attempts, so a backfill can finish with a
             * dozen bookmarks nobody has been told about, and the count is where a *Try these
             * again* eventually belongs.
             */
            val failedEnrichments: Long get() = progress.failed
        }

        /**
         * What one press of *Test connection* found out, or that it is still finding out.
         *
         * @property isRunning the call is in flight. The button says so and refuses to be
         *   pressed again — a second press would only spend the operator's allowance on a
         *   question already being asked.
         * @property ok whether the provider answered. Meaningless while [isRunning].
         * @property message the sentence to show, from the server. **Never contains the key
         *   or any part of it**, and that is enforced on the server against any provider
         *   rather than trusted — see its `AiTestRoutes`.
         */
        data class Test(
            val isRunning: Boolean = false,
            val ok: Boolean = false,
            val message: String = "",
        )

        /**
         * An open tidy plan: what was proposed, what is ticked, and what that would cost.
         *
         * ── The ticks are here rather than in the dialog ─────────────────────
         *
         * The dialog draws a checkbox per row and reports a press; what is *selected* is state,
         * and it is the state every number on the screen is derived from. A view holding it
         * would be a view deriving numbers — and three views deriving them, since a phone will
         * want the same surface.
         *
         * Two sets rather than a list of rows with a flag, because what the server is sent is
         * two lists and membership is the only question ever asked of them. It also makes a
         * tick idempotent: pressing an already-ticked row leaves the set as it was, rather
         * than appending a duplicate that would travel.
         *
         * @property plan what the server proposed, unmodified. Kept whole so the rows can be
         *   redrawn without asking again — unticking is not a reason to re-plan, which would
         *   spend the key a second time.
         * @property tickedMerges which folds are selected. Seeded from [TidyMerge.preTicked],
         *   which is the server's own judgement about which suggestions are safe by default.
         * @property tickedDrops which removals are selected. Seeded the same way.
         * @property impact what applying exactly this selection would do, **counted by the
         *   server**. Seeded from the plan's own opening count, and replaced by a fresh one on
         *   every tick.
         * @property isCounting a recount is in flight. The summary shows the last good numbers
         *   while it is true rather than blanking — a total that vanishes on every tick reads
         *   as the plan having become unknowable.
         * @property isApplying *Apply* has been pressed and the transaction has not answered.
         *   The button says so and refuses a second press.
         * @property message the server's own sentence about a refused apply or a failed
         *   recount, shown inside the dialog beside the rows it is about.
         */
        data class TidyDialog(
            val plan: TidyPlan,
            val tickedMerges: Set<TidyPair> =
                plan.merges.filter { it.preTicked }.mapTo(mutableSetOf()) {
                    TidyPair(from = it.from, to = it.to)
                },
            val tickedDrops: Set<String> =
                plan.drops.filter { it.preTicked }.mapTo(mutableSetOf()) { it.tag },
            val impact: TidyImpact = plan.impact,
            val isCounting: Boolean = false,
            val isApplying: Boolean = false,
            val message: String? = null,
        ) {
            /**
             * The ticks as the server takes them.
             *
             * **In the plan's own order**, not the sets': a `Set` has no order to preserve and
             * the request is read by a human in a log and diffed against the plan it came from,
             * both of which are easier when the two agree. It costs a filter over a few
             * hundred rows once per press.
             */
            val selection: TidySelection get() = TidySelection(
                merges = plan.merges
                    .map { TidyPair(from = it.from, to = it.to) }
                    .filter { it in tickedMerges },
                drops = plan.drops.map { it.tag }.filter { it in tickedDrops },
            )

            /** Whether anything at all is selected. Nothing ticked is nothing to apply. */
            val isEmpty: Boolean get() = tickedMerges.isEmpty() && tickedDrops.isEmpty()

            /** How many rows the plan holds altogether — the drill-down's own count. */
            val rows: Int get() = plan.merges.size + plan.drops.size
        }

        /**
         * The Connections tab: the switch, the URL, and what can act as this account.
         *
         * Already turned into the strings a pane draws — see [asMcp]. What a view adds is
         * layout, a copy button and nothing else.
         *
         * ── The switch does not hide the rest ────────────────────────────────
         *
         * A pane that revealed [serverUrl] only once agent access was on would make the setup
         * order *flip a switch you have no reason to trust yet, then go looking for the
         * command* — and the command is what most people open this tab for. The list is shown
         * either way too, and that half matters more: **an agent connected while the switch is
         * off is exactly the state somebody needs to see in order to revoke it.**
         *
         * @property isEnabled the server's answer, never this client's optimism. Flipping it
         *   takes effect on an agent's very next call in both directions — nothing is
         *   invalidated on the way off and nothing goes back through a browser on the way on.
         * @property serverUrl the absolute `/mcp` URL, **computed by the server** from the
         *   origin the request arrived on. Never assembled from the client's own location; see
         *   `McpState.serverUrl`, where the reverse-proxy case is argued. Empty only if a
         *   server answered without one.
         * @property connections what can act as this account now, oldest first.
         */
        data class Mcp(
            val isEnabled: Boolean = false,
            val serverUrl: String = "",
            val connections: List<Connection> = emptyList(),
        ) {
            /**
             * The line somebody pastes into a terminal.
             *
             * Built from [serverUrl] rather than written out, so a local run copies its own
             * localhost URL and a deployment copies the deployed one — the same argument the
             * bookmarklet makes for building itself from the origin that served the page, with
             * the difference that this origin comes from the server rather than from
             * `location`. Nobody types these correctly, which is why it exists at all.
             */
            val installCommand: String
                get() = "claude mcp add --transport http lunapin $serverUrl"

            /** Whether anything is connected. Empty is the common case and draws a sentence. */
            val hasConnections: Boolean get() = connections.isNotEmpty()

            /**
             * One connected agent, as two strings and an id.
             *
             * @property clientId what Revoke names. Not a secret.
             * @property name what the agent calls itself. **Self-reported**, so a view must
             *   render it as text and never as markup — see `McpConnection.clientName`. It is
             *   deliberately not the only thing shown: [detail] carries the dates, which are
             *   what actually tell somebody whether they recognise a row, and what keeps two
             *   agents calling themselves the same thing apart.
             * @property detail "Connected 3 days ago · last used 2 minutes ago".
             */
            data class Connection(
                val clientId: String,
                val name: String,
                val detail: String,
            )
        }

        data class Failed(val message: String)
    }

    private companion object {
        /** Only reached when the failure carried no sentence of its own. */
        const val COULD_NOT_LOAD = "Could not load your settings."
        const val COULD_NOT_SAVE = "Could not save that."
        const val COULD_NOT_BACKFILL = "Could not queue those bookmarks."
        const val COULD_NOT_CANCEL = "Could not stop the backfill."
        const val COULD_NOT_TEST = "Could not test the connection."
        const val COULD_NOT_PLAN = "Could not work out what to tidy."
        const val COULD_NOT_COUNT = "Could not work out what that would do."
        const val COULD_NOT_TIDY = "Could not tidy those tags. Nothing was changed."
        const val COULD_NOT_UNDO = "Could not undo the last tidy."
        const val COULD_NOT_SET_AGENT_ACCESS = "Could not change agent access."
        const val COULD_NOT_REVOKE = "Could not disconnect that agent."

        /**
         * A plan with nothing in it.
         *
         * Said in words rather than by opening a dialog listing no changes, whose only
         * affordance would be *Cancel*. It names why rather than merely reporting nothing:
         * "no duplicates and nothing used once" is the answer somebody with 1182 tags would
         * want explained, and it is also true of an account with four.
         */
        const val NOTHING_TO_TIDY =
            "Nothing to tidy — no singular/plural duplicates, nothing used only once, and " +
                "nothing else worth folding together."

        /**
         * How long the *Planning…* counter keeps counting.
         *
         * Four minutes, which is the server's own ceiling on the model call rather than a
         * number chosen here: past it the request has already failed and a message is on its
         * way, so a counter still climbing would be the screen disagreeing with itself.
         *
         * It is a bound rather than a timeout — nothing is cancelled when it is reached, the
         * count simply stops moving — because the request belongs to the server and this side
         * giving up on it independently would leave a plan nobody is waiting for.
         */
        const val LONGEST_TIDY_WAIT_SECONDS = 240
    }
}

/**
 * The settings as the request that stores them.
 *
 * `aiAvailable` is absent, and that is the type system carrying the rule rather than a
 * comment asking for it: it is not this account's to choose, so there is no field on
 * [SetAccountSettingsRequest] for a client to disagree with the server through.
 */
private fun AccountSettings.asRequest() = SetAccountSettingsRequest(
    aiSummariesEnabled = aiSummariesEnabled,
    aiTagsEnabled = aiTagsEnabled,
    aiTagVocabularyTarget = aiTagVocabularyTarget,
    aiSummaryLength = aiSummaryLength,
    aiLanguage = aiLanguage,
    saveQuickTags = saveQuickTags,
    saveDefaultTags = saveDefaultTags,
)
